(() => {
if (customElements.get('juniper-visit-gallery')) return;

class JuniperVisitGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.dialog = this.querySelector('.jvg-dialog');
    this.trigger = this.querySelector('.jvg-trigger');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.currentOutput = this.querySelector('[data-gallery-current]');
    this.index = 0;
    if (!this.dialog || this.slides.length === 0) return;

    const options = { signal: this.controller.signal };
    this.trigger?.addEventListener('click', () => this.open(), options);
    this.querySelector('.jvg-close')?.addEventListener('click', () => this.close(), options);
    this.querySelector('.jvg-prev')?.addEventListener('click', () => this.show(this.index - 1), options);
    this.querySelector('.jvg-next')?.addEventListener('click', () => this.show(this.index + 1), options);
    this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
    this.dialog.addEventListener('close', () => this.restorePage(), options);
    this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); }, options);
    this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), options);
    this.dialog.addEventListener('touchstart', (event) => this.onTouchStart(event), { ...options, passive: true });
    this.dialog.addEventListener('touchend', (event) => this.onTouchEnd(event), { ...options, passive: true });
    this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), options);
    this.addEventListener('shopify:block:deselect', (event) => this.onBlockDeselect(event), options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    if (this.dialog?.open) this.dialog.close();
    this.restorePage(false);
  }

  open(nextIndex = this.index, editorPreview = false) {
    this.show(nextIndex);
    if (this.dialog.open) return;
    this.editorPreviewOpen = editorPreview;
    if (!editorPreview) {
      this.returnFocus = document.activeElement;
      this.lockPage();
    }
    this.dialog.showModal();
    if (!editorPreview) this.querySelector('.jvg-close')?.focus();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
    else this.restorePage();
    this.editorPreviewOpen = false;
  }

  onBlockSelect(event) {
    const slide = event.target?.closest?.('[data-gallery-slide]');
    const index = this.slides.indexOf(slide);
    if (index !== -1) this.open(index, true);
  }

  onBlockDeselect(event) {
    const slide = event.target?.closest?.('[data-gallery-slide]');
    if (this.editorPreviewOpen && this.slides.includes(slide)) this.close();
  }

  show(nextIndex) {
    const length = this.slides.length;
    this.index = ((nextIndex % length) + length) % length;
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.hidden = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    if (this.currentOutput) this.currentOutput.textContent = String(this.index + 1);
    const image = this.slides[this.index]?.querySelector('img');
    if (image) {
      image.loading = 'eager';
      image.decode?.().catch(() => {});
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft' && this.slides.length > 1) { event.preventDefault(); this.show(this.index - 1); }
    if (event.key === 'ArrowRight' && this.slides.length > 1) { event.preventDefault(); this.show(this.index + 1); }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  onTouchStart(event) {
    const touch = event.changedTouches?.[0];
    if (!touch || event.target.closest?.('button')) { this.touchStart = null; return; }
    this.touchStart = { x: touch.clientX, y: touch.clientY };
  }

  onTouchEnd(event) {
    if (!this.touchStart || this.slides.length < 2) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const dx = touch.clientX - this.touchStart.x;
    const dy = touch.clientY - this.touchStart.y;
    this.touchStart = null;
    if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
    this.show(this.index + (dx < 0 ? 1 : -1));
  }

  trapFocus(event) {
    const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]):not([hidden]), [href], [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.closest('[hidden]'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  lockPage() {
    if (this.pageState) return;
    this.restoreToken = (this.restoreToken || 0) + 1;
    this.pageState = {
      htmlStyle: document.documentElement.getAttribute('style'),
      bodyStyle: document.body.getAttribute('style'),
      x: window.scrollX,
      y: window.scrollY
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.inset = `${-this.pageState.y}px auto auto ${-this.pageState.x}px`;
    document.body.style.width = '100%';
    document.body.classList.add('jvg-scroll-locked');
  }

  restorePage(returnFocus = true) {
    if (!this.pageState) return;
    const state = this.pageState;
    const returnTarget = this.returnFocus;
    const restoreToken = (this.restoreToken || 0) + 1;
    this.restoreToken = restoreToken;
    this.pageState = null;
    this.restoreStyle(document.documentElement, state.htmlStyle);
    this.restoreStyle(document.body, state.bodyStyle);
    document.body.classList.remove('jvg-scroll-locked');
    const finishRestore = () => {
      if (this.restoreToken !== restoreToken || this.pageState) return;
      if (returnFocus && returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
      window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
    };
    finishRestore();
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(finishRestore));
    this.returnFocus = null;
  }

  restoreStyle(element, value) {
    if (value === null) element.removeAttribute('style');
    else element.setAttribute('style', value);
  }
}

customElements.define('juniper-visit-gallery', JuniperVisitGallery);
})();
